// Subtitles guide — the ⋮ menu's Subtitles modal (SubtitlesModal over the
// useSubtitles hook) and where the generated subtitles show up: the Visor
// player's captions, Video Export and the Export "show by default" toggle.
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const subtitlesGuide = {
  id: "subtitles",
  icon: "subtitles",
  keywords: [
    "subtitles",
    "altyazı",
    "altyazılar",
    "captions",
    "cc",
    "language",
    "dil",
    "translate",
    "çeviri",
    "force regenerate",
    "zorla yeniden üret",
    "narration",
    "seslendirme",
    "video export",
    "video dışa aktarma",
    "subtítulos",
    "idioma",
    "traducir",
    "traducción",
    "forzar regeneración",
    "narración",
    "exportar vídeo",
  ],
  content: {
    en: {
      feature: "Publishing",
      title: "Subtitles",
      sections: [
        {
          id: "overview",
          title: "Opening Subtitles",
          videoUrl: null,
          text:
            "Open the **⋮** menu in the top navbar and choose **Subtitles**. Subtitles are created per scene from the scenes' **narration audio**, so a scene without narration gets no subtitle.\n\n" +
            "Subtitles are generated from the **saved** training — save first so your latest narration is included.",
        },
        {
          id: "languages",
          title: "Available languages",
          videoUrl: null,
          text:
            "**Available Languages** lists every language that already has subtitles, each with a count of covered scenes out of the total (e.g. 8/10). A check mark means every scene is covered; a warning icon means some scenes are missing. Use **Refresh** to reload the list.\n\n" +
            "Click a language to see its subtitle text scene by scene. Scenes without one show **Subtitle not found for this scene**.",
        },
        {
          id: "generate",
          title: "Generate subtitles",
          videoUrl: null,
          text:
            "Under **Generate Subtitles**, choose one of 12 languages — Turkish, English, French, German, Spanish, Italian, Portuguese, Arabic, Russian, Azerbaijani, Japanese or Korean — and press **Generate**.\n\n" +
            "- If that language already has subtitles, tick **Force regenerate** to overwrite them.\n" +
            "- Generation runs as a background job and may use credits; a progress bar shows the percentage.\n" +
            "- If you close the window, the job keeps running — reopen **Subtitles** later and the new language appears once it has finished.",
        },
        {
          id: "delete",
          title: "Delete a language",
          videoUrl: null,
          text: "Click the trash icon on a language and confirm **Delete** to remove all of its subtitles. This cannot be undone. Videos you have already exported keep the captions that were burned into them.",
        },
        {
          id: "where-used",
          title: "Where subtitles appear",
          videoUrl: null,
          text:
            "- **Preview and the learner player** — a **Subtitles** button appears in the player bar (in the **⋯** menu on narrow screens) once at least one language exists. With one language it turns captions on and off; with several it opens a language menu. It is disabled on scenes without narration.\n" +
            "- **Video Export** — when starting a video, **Subtitle language (optional)** burns the chosen language into the video.\n" +
            "- **Export** — for SCORM and URL exports, **Show subtitles automatically when the training opens.** turns captions on by default for learners.",
        },
      ],
    },
    tr: {
      feature: "Yayımlama",
      title: "Altyazılar",
      sections: [
        {
          id: "overview",
          title: "Altyazılar'ı açma",
          videoUrl: null,
          text:
            "Üst gezinme çubuğundaki **⋮** menüsünü açın ve **Altyazılar**'ı seçin. Altyazılar sahne bazında, sahnelerin **seslendirme sesinden** oluşturulur; seslendirmesi olmayan sahnenin altyazısı olmaz.\n\n" +
            "Altyazılar eğitimin **kaydedilmiş** halinden üretilir — en güncel seslendirmenin dahil olması için önce kaydedin.",
        },
        {
          id: "languages",
          title: "Mevcut diller",
          videoUrl: null,
          text:
            "**Mevcut Diller**, altyazısı bulunan her dili, kapsanan sahne sayısının toplama oranıyla (ör. 8/10) listeler. Onay işareti tüm sahnelerin kapsandığını, uyarı simgesi bazı sahnelerin eksik olduğunu gösterir. Listeyi yeniden yüklemek için **Yenile**'yi kullanın.\n\n" +
            "Bir dile tıklayarak altyazı metnini sahne sahne görebilirsiniz. Altyazısı olmayan sahnelerde **Bu sahne için altyazı bulunamadı** yazar.",
        },
        {
          id: "generate",
          title: "Altyazı üretme",
          videoUrl: null,
          text:
            "**Altyazı Üret** bölümünde 12 dilden birini seçin — Türkçe, İngilizce, Fransızca, Almanca, İspanyolca, İtalyanca, Portekizce, Arapça, Rusça, Azerice, Japonca veya Korece — ve **Üret**'e basın.\n\n" +
            "- Seçilen dilde zaten altyazı varsa, üzerine yazmak için **Zorla yeniden üret**'i işaretleyin.\n" +
            "- Üretim arka planda bir iş olarak çalışır ve kredi kullanabilir; ilerleme çubuğu yüzdeyi gösterir.\n" +
            "- Pencereyi kapatsanız da iş devam eder — **Altyazılar**'ı daha sonra yeniden açtığınızda, iş bitmişse yeni dil listede görünür.",
        },
        {
          id: "delete",
          title: "Bir dili silme",
          videoUrl: null,
          text: "Bir dilin üzerindeki çöp kutusu simgesine tıklayıp **Sil** ile onaylayarak o dildeki tüm altyazıları kaldırın. Bu işlem geri alınamaz. Daha önce dışa aktardığınız videolar, içlerine işlenmiş altyazıları korur.",
        },
        {
          id: "where-used",
          title: "Altyazılar nerede görünür",
          videoUrl: null,
          text:
            "- **Ön izleme ve öğrenci oynatıcısı** — en az bir dil olduğunda oynatıcı çubuğunda (dar ekranlarda **⋯** menüsünde) **Altyazılar** düğmesi görünür. Tek dil varsa altyazıyı açıp kapatır; birden fazla dil varsa dil menüsünü açar. Seslendirmesi olmayan sahnelerde devre dışıdır.\n" +
            "- **Video Dışa Aktarma** — video başlatılırken **Altyazı dili (opsiyonel)** seçilen dili videonun içine işler.\n" +
            "- **Yayımla** — SCORM ve URL dışa aktarımlarında **Eğitim açıldığında alt yazıyı otomatik olarak göster.** seçeneği, altyazıları öğrenciler için varsayılan olarak açar.",
        },
      ],
    },
    es: {
      feature: "Publicación",
      title: "Subtítulos",
      sections: [
        {
          id: "overview",
          title: "Abrir Subtítulos",
          videoUrl: null,
          text:
            "Abre el menú **⋮** de la barra de navegación superior y elige **Subtítulos**. Los subtítulos se crean por escena a partir del **audio de narración** de las escenas, así que una escena sin narración no tiene subtítulos.\n\n" +
            "Los subtítulos se generan a partir de la formación **guardada**: guarda primero para que se incluya tu narración más reciente.",
        },
        {
          id: "languages",
          title: "Idiomas disponibles",
          videoUrl: null,
          text:
            "**Idiomas Disponibles** enumera todos los idiomas que ya tienen subtítulos, cada uno con el número de escenas cubiertas sobre el total (p. ej., 8/10). Una marca de verificación indica que todas las escenas están cubiertas; un icono de advertencia, que faltan algunas. Usa **Actualizar** para recargar la lista.\n\n" +
            "Haz clic en un idioma para ver su texto de subtítulos escena por escena. Las escenas que no lo tienen muestran **No se encontró subtítulo para esta escena**.",
        },
        {
          id: "generate",
          title: "Generar subtítulos",
          videoUrl: null,
          text:
            "En **Generar Subtítulos**, elige uno de los 12 idiomas (turco, inglés, francés, alemán, español, italiano, portugués, árabe, ruso, azerbaiyano, japonés o coreano) y pulsa **Generar**.\n\n" +
            "- Si ese idioma ya tiene subtítulos, marca **Forzar regeneración** para sobrescribirlos.\n" +
            "- La generación se ejecuta como un trabajo en segundo plano y puede consumir créditos; una barra de progreso muestra el porcentaje.\n" +
            "- Si cierras la ventana, el trabajo sigue en marcha: vuelve a abrir **Subtítulos** más tarde y el nuevo idioma aparecerá cuando haya terminado.",
        },
        {
          id: "delete",
          title: "Eliminar un idioma",
          videoUrl: null,
          text: "Haz clic en el icono de la papelera de un idioma y confirma con **Eliminar** para borrar todos sus subtítulos. Esta acción no se puede deshacer. Los vídeos que ya hayas exportado conservan los subtítulos incrustados en ellos.",
        },
        {
          id: "where-used",
          title: "Dónde aparecen los subtítulos",
          videoUrl: null,
          text:
            "- **Vista previa y reproductor del alumno**: aparece un botón **Subtítulos** en la barra del reproductor (en el menú **⋯** en pantallas estrechas) en cuanto existe al menos un idioma. Con un solo idioma, activa y desactiva los subtítulos; con varios, abre un menú de idiomas. Está desactivado en las escenas sin narración.\n" +
            "- **Exportar video**: al iniciar un vídeo, **Idioma de subtítulos (opcional)** incrusta en el vídeo el idioma elegido.\n" +
            "- **Exportar...**: en las exportaciones SCORM y URL, **Mostrar los subtítulos automáticamente al abrir la formación.** activa los subtítulos de forma predeterminada para los alumnos.",
        },
      ],
    },
  },
};

export default subtitlesGuide;
